import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveCompanyWithSirenGuard, askSiren } from '../lib/companies';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { capitalizeFirst } from '../utils/capitalize';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { SearchableCombobox } from '../components/ui/searchable-combobox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
    Users,
    Plus,
    Search,
    MoreHorizontal,
    Eye,
    Pencil,
    Trash2,
    Building2,
    Mail,
    Phone,
    Smartphone,
    Linkedin,
    ExternalLink,
    Loader2,
    Star,
    Filter,
    Upload,
    Download,
    FileSpreadsheet,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    X,
    UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const Contacts = () => {
    const { api, user, isSuperAdmin } = useAuth();
    const navigate = useNavigate();
    
    const [contacts, setContacts] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [companyFilter, setCompanyFilter] = useState('all');
    
    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    
    // Import state
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    
    // Selection state
    const [selectedIds, setSelectedIds] = useState([]);
    const [showBulkActionModal, setShowBulkActionModal] = useState(false);
    const [bulkAction, setBulkAction] = useState('');
    const [bulkActionValue, setBulkActionValue] = useState('');
    
    // Form state
    const [formData, setFormData] = useState({
        company_id: '',
        civility: '',
        first_name: '',
        last_name: '',
        job_title: '',
        email: '',
        phone: '',
        mobile: '',
        linkedin: '',
        is_primary: false,
        notes: '',
    });

    useEffect(() => {
        fetchData();
    }, [companyFilter]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (companyFilter && companyFilter !== 'all') {
                params.append('company_id', companyFilter);
            }
            
            const [contactsRes, companiesRes] = await Promise.all([
                api.get(`/contacts?${params.toString()}`),
                api.get('/companies'),
            ]);
            
            setContacts(contactsRes.data);
            setCompanies(companiesRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            const submitData = {
                ...formData,
                civility: formData.civility && formData.civility !== 'none' ? formData.civility : null,
            };
            await api.post('/contacts', submitData);
            toast.success('Contact créé');
            setShowCreateModal(false);
            resetForm();
            fetchData();
        } catch (error) {
            console.error('Failed to create contact:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            await api.patch(`/contacts/${selectedContact.id}`, formData);
            toast.success('Contact mis à jour');
            setShowEditModal(false);
            fetchData();
        } catch (error) {
            console.error('Failed to update contact:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setSubmitting(true);
        
        try {
            await api.delete(`/contacts/${selectedContact.id}`);
            toast.success('Contact supprimé');
            setShowDeleteModal(false);
            fetchData();
        } catch (error) {
            console.error('Failed to delete contact:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setSubmitting(false);
        }
    };

    // Quick create company from contact form
    const handleQuickCreateCompany = async (name) => {
        try {
            const siret = askSiren(name);
            if (siret === null) return;
            if (!siret) {
                toast.error('SIREN (9 chiffres) ou SIRET (14 chiffres) obligatoire');
                return;
            }
            const response = await saveCompanyWithSirenGuard(
                (force) => api.post(`/companies${force ? '?force=true' : ''}`, { legal_name: name, siret, statuses: ['prospect'] }),
                { api, canForce: isSuperAdmin() }
            );
            toast.success(`Entreprise "${name}" créée`);
            // Refresh companies and select the new one
            const companiesRes = await api.get('/companies');
            setCompanies(companiesRes.data);
            setFormData(prev => ({ ...prev, company_id: response.data.id }));
        } catch (error) {
            console.error('Failed to create company:', error);
            if (!error.handled) toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'entreprise');
        }
    };

    const resetForm = () => {
        setFormData({
            company_id: '',
            civility: '',
            first_name: '',
            last_name: '',
            job_title: '',
            email: '',
            phone: '',
            mobile: '',
            linkedin: '',
            is_primary: false,
            notes: '',
        });
    };

    // Import functions
    const handleImport = async () => {
        if (!importFile) {
            toast.error('Veuillez sélectionner un fichier');
            return;
        }

        setImporting(true);
        setImportResult(null);

        try {
            const formData = new FormData();
            formData.append('file', importFile);
            formData.append('skip_duplicates', skipDuplicates);

            const response = await api.post('/contacts/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setImportResult(response.data);
            
            if (response.data.imported > 0) {
                toast.success(`${response.data.imported} contact(s) importé(s) avec succès`);
                fetchData();
            }
            
            if (response.data.skipped > 0) {
                toast.info(`${response.data.skipped} doublon(s) ignoré(s)`);
            }
            
            if (response.data.errors.length > 0) {
                toast.warning(`${response.data.errors.length} erreur(s) lors de l'import`);
            }
        } catch (error) {
            console.error('Import failed:', error);
            toast.error(error.response?.data?.detail || "Erreur lors de l'import");
        } finally {
            setImporting(false);
        }
    };

    const downloadTemplate = async (format) => {
        try {
            const response = await api.get(`/contacts/import/template?format=${format}`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `modele_import_contacts.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Modèle téléchargé');
        } catch (error) {
            console.error('Download failed:', error);
            toast.error('Erreur lors du téléchargement');
        }
    };

    const resetImportModal = () => {
        setImportFile(null);
        setImportResult(null);
        setSkipDuplicates(true);
        setShowImportModal(false);
    };

    // Selection functions
    const toggleSelection = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === filteredContacts.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredContacts.map(c => c.id));
        }
    };

    const clearSelection = () => {
        setSelectedIds([]);
    };

    const openBulkActionModal = (action) => {
        setBulkAction(action);
        setBulkActionValue('');
        setShowBulkActionModal(true);
    };

    const handleBulkAction = async () => {
        if (selectedIds.length === 0) return;
        
        setSubmitting(true);
        try {
            const payload = {
                contact_ids: selectedIds,
                action: bulkAction,
                value: bulkActionValue || null,
            };
            
            const response = await api.post('/contacts/bulk-action', payload);
            
            if (response.data.success > 0) {
                toast.success(`${response.data.success} contact(s) modifié(s)`);
                fetchData();
                clearSelection();
            }
            if (response.data.failed > 0) {
                toast.error(`${response.data.failed} erreur(s)`);
            }
            
            setShowBulkActionModal(false);
        } catch (error) {
            console.error('Bulk action failed:', error);
            toast.error(error.response?.data?.detail || "Erreur lors de l'action");
        } finally {
            setSubmitting(false);
        }
    };

    const openEditModal = (contact) => {
        setSelectedContact(contact);
        setFormData({
            company_id: contact.company_id,
            civility: contact.civility || '',
            first_name: contact.first_name,
            last_name: contact.last_name,
            job_title: contact.job_title || '',
            email: contact.email || '',
            phone: contact.phone || '',
            mobile: contact.mobile || '',
            linkedin: contact.linkedin || '',
            is_primary: contact.is_primary || false,
            notes: contact.notes || '',
        });
        setShowEditModal(true);
    };

    const filteredContacts = contacts.filter(c => {
        if (!searchQuery) return true;
        const search = searchQuery.toLowerCase();
        return (
            c.first_name.toLowerCase().includes(search) ||
            c.last_name.toLowerCase().includes(search) ||
            c.email?.toLowerCase().includes(search) ||
            c.company_name?.toLowerCase().includes(search) ||
            c.job_title?.toLowerCase().includes(search)
        );
    });

    const totalContacts = contacts.length;
    const primaryContacts = contacts.filter(c => c.is_primary).length;

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-40" />
                </div>
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="contacts-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold font-heading">Contacts</h1>
                    <p className="text-muted-foreground mt-1">
                        {totalContacts} contacts • {primaryContacts} principaux
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setShowImportModal(true)}
                        className="rounded-full"
                        data-testid="import-contacts-btn"
                    >
                        <Upload className="w-4 h-4 mr-2" />
                        Importer
                    </Button>
                    <Button
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="rounded-full"
                        data-testid="create-contact-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouveau contact
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher par nom, email, entreprise..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 rounded-lg"
                                data-testid="contacts-search-input"
                            />
                        </div>
                        <Select value={companyFilter} onValueChange={setCompanyFilter}>
                            <SelectTrigger className="w-full sm:w-64 rounded-lg" data-testid="company-filter">
                                <Building2 className="w-4 h-4 mr-2" />
                                <SelectValue placeholder="Filtrer par entreprise" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                                <div className="px-2 py-2 sticky top-0 bg-popover">
                                    <Input
                                        placeholder="Rechercher..."
                                        className="h-8 rounded-md"
                                        onChange={(e) => {
                                            const search = e.target.value.toLowerCase();
                                            const items = document.querySelectorAll('[data-company-item]');
                                            items.forEach((item) => {
                                                const text = item.textContent.toLowerCase();
                                                item.style.display = text.includes(search) ? 'flex' : 'none';
                                            });
                                        }}
                                    />
                                </div>
                                <SelectItem value="all">Toutes les entreprises</SelectItem>
                                {companies.map((c) => (
                                    <SelectItem key={c.id} value={c.id} data-company-item>{c.legal_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Selection Actions Bar */}
            {selectedIds.length > 0 && (
                <Card className="border-primary/50 bg-primary/5">
                    <CardContent className="py-3 px-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <span className="font-medium">
                                    {selectedIds.length} contact(s) sélectionné(s)
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={clearSelection}
                                    className="rounded-full"
                                >
                                    <X className="w-4 h-4 mr-1" />
                                    Désélectionner
                                </Button>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openBulkActionModal('assign_company')}
                                    className="rounded-full"
                                >
                                    <Building2 className="w-4 h-4 mr-2" />
                                    Changer d'entreprise
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openBulkActionModal('set_primary')}
                                    className="rounded-full"
                                >
                                    <UserCheck className="w-4 h-4 mr-2" />
                                    Principal
                                </Button>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => openBulkActionModal('delete')}
                                    className="rounded-full"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Supprimer
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Contacts Table */}
            <Card>
                <CardContent className="p-0">
                    {filteredContacts.length === 0 ? (
                        <div className="text-center py-12">
                            <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                            <p className="text-muted-foreground">Aucun contact trouvé</p>
                            <Button
                                variant="link"
                                onClick={() => {
                                    resetForm();
                                    setShowCreateModal(true);
                                }}
                                className="mt-2"
                            >
                                Créer un contact
                            </Button>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[60px] pl-6">
                                        <Checkbox
                                            checked={selectedIds.length === filteredContacts.length && filteredContacts.length > 0}
                                            onCheckedChange={toggleSelectAll}
                                            aria-label="Sélectionner tout"
                                        />
                                    </TableHead>
                                    <TableHead>Nom</TableHead>
                                    <TableHead>Entreprise</TableHead>
                                    <TableHead>Fonction</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Téléphone</TableHead>
                                    <TableHead className="text-right pr-6">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredContacts.map((contact) => (
                                    <TableRow 
                                        key={contact.id} 
                                        data-testid={`contact-row-${contact.id}`}
                                        className={selectedIds.includes(contact.id) ? 'bg-primary/5' : ''}
                                    >
                                        <TableCell className="pl-6" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={selectedIds.includes(contact.id)}
                                                onCheckedChange={() => toggleSelection(contact.id)}
                                                aria-label={`Sélectionner ${contact.first_name} ${contact.last_name}`}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                                                    {contact.first_name[0]}{contact.last_name[0]}
                                                </div>
                                                <div>
                                                    <div className="font-medium flex items-center gap-1">
                                                        {contact.civility && <span className="text-muted-foreground">{contact.civility}</span>}
                                                        {contact.first_name} {contact.last_name}
                                                        {contact.is_primary && (
                                                            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Link 
                                                to={`/companies/${contact.company_id}`}
                                                className="hover:underline flex items-center gap-1 text-sm"
                                            >
                                                <Building2 className="w-3 h-3" />
                                                {contact.company_name}
                                            </Link>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {contact.job_title || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {contact.email ? (
                                                <a 
                                                    href={`mailto:${contact.email}`}
                                                    className="flex items-center gap-1 text-sm hover:underline"
                                                >
                                                    <Mail className="w-3 h-3" />
                                                    {contact.email}
                                                </a>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1">
                                                {contact.phone && (
                                                    <a href={`tel:${contact.phone}`} className="flex items-center gap-1 text-sm hover:underline">
                                                        <Phone className="w-3 h-3" />
                                                        {contact.phone}
                                                    </a>
                                                )}
                                                {contact.mobile && (
                                                    <a href={`tel:${contact.mobile}`} className="flex items-center gap-1 text-sm hover:underline">
                                                        <Smartphone className="w-3 h-3" />
                                                        {contact.mobile}
                                                    </a>
                                                )}
                                                {!contact.phone && !contact.mobile && '-'}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center justify-end gap-1">
                                                {/* LinkedIn icon - only shows if linkedin exists */}
                                                {contact.linkedin && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 rounded-full text-[#0077B5] hover:text-[#0077B5] hover:bg-[#0077B5]/10"
                                                        asChild
                                                        title="Voir le profil LinkedIn"
                                                    >
                                                        <a href={contact.linkedin} target="_blank" rel="noopener noreferrer">
                                                            <Linkedin className="w-4 h-4" />
                                                        </a>
                                                    </Button>
                                                )}
                                                {/* View company icon */}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 rounded-full"
                                                    onClick={() => navigate(`/companies/${contact.company_id}`)}
                                                    title="Voir l'entreprise"
                                                >
                                                    <Building2 className="w-4 h-4" />
                                                </Button>
                                                {/* View contact icon */}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 rounded-full"
                                                    onClick={() => {
                                                        // Navigate to company page showing this contact
                                                        navigate(`/companies/${contact.company_id}?contact=${contact.id}`);
                                                    }}
                                                    title="Voir le contact"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {/* Edit icon */}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 rounded-full"
                                                    onClick={() => openEditModal(contact)}
                                                    title="Modifier"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                {/* Delete icon (admin only) */}
                                                {isSuperAdmin() && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 rounded-full text-destructive hover:text-destructive"
                                                        onClick={() => {
                                                            setSelectedContact(contact);
                                                            setShowDeleteModal(true);
                                                        }}
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Modal */}
            <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
                if (!open) {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                }
            }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {showEditModal ? 'Modifier le contact' : 'Nouveau contact'}
                        </DialogTitle>
                        <DialogDescription>
                            {showEditModal ? 'Mettez à jour les informations du contact' : 'Ajoutez un nouveau contact'}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={showEditModal ? handleUpdate : handleCreate} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Entreprise *</Label>
                            <SearchableCombobox
                                value={formData.company_id}
                                onValueChange={(v) => setFormData({ ...formData, company_id: v })}
                                options={companies.map(c => ({
                                    id: c.id,
                                    label: c.legal_name,
                                    sublabel: c.city || c.sector,
                                }))}
                                placeholder="Rechercher une entreprise..."
                                searchPlaceholder="Tapez pour rechercher..."
                                emptyMessage="Aucune entreprise trouvée"
                                createLabel="Créer l'entreprise"
                                onCreate={handleQuickCreateCompany}
                                type="company"
                                data-testid="contact-company-select"
                            />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Civilité</Label>
                                <Select
                                    value={formData.civility}
                                    onValueChange={(v) => setFormData({ ...formData, civility: v })}
                                >
                                    <SelectTrigger className="rounded-lg">
                                        <SelectValue placeholder="--" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">--</SelectItem>
                                        <SelectItem value="M.">M.</SelectItem>
                                        <SelectItem value="Mme">Mme</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Prénom *</Label>
                                <Input
                                    value={formData.first_name}
                                    onChange={(e) => setFormData({ ...formData, first_name: capitalizeFirst(e.target.value) })}
                                    required
                                    className="rounded-lg"
                                    data-testid="contact-firstname-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Nom *</Label>
                                <Input
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: capitalizeFirst(e.target.value) })}
                                    required
                                    className="rounded-lg"
                                    data-testid="contact-lastname-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Fonction</Label>
                            <Input
                                value={formData.job_title}
                                onChange={(e) => setFormData({ ...formData, job_title: capitalizeFirst(e.target.value) })}
                                placeholder="Ex: Directeur Marketing"
                                className="rounded-lg"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="email@entreprise.fr"
                                className="rounded-lg"
                                data-testid="contact-email-input"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Téléphone fixe</Label>
                                <Input
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    placeholder="01 23 45 67 89"
                                    className="rounded-lg"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Mobile</Label>
                                <Input
                                    value={formData.mobile}
                                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                                    placeholder="06 12 34 56 78"
                                    className="rounded-lg"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>LinkedIn</Label>
                            <Input
                                value={formData.linkedin}
                                onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                                placeholder="https://linkedin.com/in/..."
                                className="rounded-lg"
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="is_primary"
                                checked={formData.is_primary}
                                onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                                className="rounded"
                            />
                            <Label htmlFor="is_primary" className="text-sm font-normal cursor-pointer">
                                Contact principal de l'entreprise
                            </Label>
                        </div>

                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                rows={2}
                                placeholder="Notes sur ce contact..."
                                className="rounded-lg"
                            />
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setShowEditModal(false);
                                    resetForm();
                                }}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full" data-testid="contact-submit-btn">
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {showEditModal ? 'Mise à jour...' : 'Création...'}
                                    </>
                                ) : (
                                    showEditModal ? 'Mettre à jour' : 'Créer'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Confirmer la suppression</DialogTitle>
                        <DialogDescription>
                            Êtes-vous sûr de vouloir supprimer le contact "{selectedContact?.first_name} {selectedContact?.last_name}" ?
                            Cette action est irréversible.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowDeleteModal(false)}
                            className="rounded-full"
                        >
                            Annuler
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={submitting}
                            className="rounded-full"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Supprimer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import Modal */}
            <Dialog open={showImportModal} onOpenChange={(open) => !open && resetImportModal()}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5" />
                            Importer des contacts
                        </DialogTitle>
                        <DialogDescription>
                            Importez vos contacts depuis un fichier CSV ou Excel (XLS/XLSX).
                            Les entreprises doivent exister au préalable.
                        </DialogDescription>
                    </DialogHeader>

                    {!importResult ? (
                        <div className="space-y-4">
                            {/* Download template */}
                            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                <div className="flex items-center gap-2 text-sm">
                                    <Download className="w-4 h-4 text-muted-foreground" />
                                    <span>Télécharger le modèle</span>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => downloadTemplate('csv')}
                                        className="rounded-full"
                                    >
                                        CSV
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => downloadTemplate('xlsx')}
                                        className="rounded-full"
                                    >
                                        Excel
                                    </Button>
                                </div>
                            </div>

                            {/* File upload */}
                            <div className="space-y-2">
                                <Label>Fichier à importer</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="file"
                                        accept=".csv,.xls,.xlsx"
                                        onChange={(e) => setImportFile(e.target.files[0])}
                                        className="rounded-lg"
                                        data-testid="import-file-input"
                                    />
                                </div>
                                {importFile && (
                                    <p className="text-sm text-muted-foreground">
                                        Fichier sélectionné: {importFile.name}
                                    </p>
                                )}
                            </div>

                            {/* Options */}
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="skip-duplicates-contacts"
                                    checked={skipDuplicates}
                                    onCheckedChange={setSkipDuplicates}
                                />
                                <label
                                    htmlFor="skip-duplicates-contacts"
                                    className="text-sm font-medium leading-none cursor-pointer"
                                >
                                    Ignorer les doublons (même nom + entreprise ou email)
                                </label>
                            </div>

                            {/* Column info */}
                            <Alert>
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription className="text-xs">
                                    <strong>Colonnes reconnues :</strong> entreprise (obligatoire), prenom (obligatoire), nom (obligatoire), email, telephone, mobile, fonction, linkedin, notes, principal (oui/non)
                                </AlertDescription>
                            </Alert>

                            <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
                                <Building2 className="h-4 w-4 text-blue-500" />
                                <AlertDescription className="text-xs text-blue-700 dark:text-blue-300">
                                    <strong>Important :</strong> Le nom de l'entreprise doit correspondre exactement à une entreprise existante (raison sociale ou nom commercial).
                                </AlertDescription>
                            </Alert>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Results summary */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="text-center p-3 bg-green-500/10 rounded-lg">
                                    <CheckCircle2 className="w-6 h-6 mx-auto text-green-500 mb-1" />
                                    <div className="text-2xl font-bold text-green-600">{importResult.imported}</div>
                                    <div className="text-xs text-muted-foreground">Importés</div>
                                </div>
                                <div className="text-center p-3 bg-yellow-500/10 rounded-lg">
                                    <AlertTriangle className="w-6 h-6 mx-auto text-yellow-500 mb-1" />
                                    <div className="text-2xl font-bold text-yellow-600">{importResult.skipped}</div>
                                    <div className="text-xs text-muted-foreground">Doublons</div>
                                </div>
                                <div className="text-center p-3 bg-red-500/10 rounded-lg">
                                    <XCircle className="w-6 h-6 mx-auto text-red-500 mb-1" />
                                    <div className="text-2xl font-bold text-red-600">{importResult.errors.length}</div>
                                    <div className="text-xs text-muted-foreground">Erreurs</div>
                                </div>
                            </div>

                            {/* Progress bar */}
                            <div className="space-y-1">
                                <div className="flex justify-between text-sm">
                                    <span>Progression</span>
                                    <span>{importResult.imported + importResult.skipped + importResult.errors.length} / {importResult.total_rows}</span>
                                </div>
                                <Progress 
                                    value={((importResult.imported + importResult.skipped + importResult.errors.length) / importResult.total_rows) * 100} 
                                    className="h-2"
                                />
                            </div>

                            {/* Duplicates list */}
                            {importResult.duplicates.length > 0 && (
                                <div className="space-y-2">
                                    <Label className="text-yellow-600">Doublons ignorés :</Label>
                                    <div className="max-h-24 overflow-y-auto text-sm text-muted-foreground bg-muted/30 p-2 rounded">
                                        {importResult.duplicates.slice(0, 10).join(', ')}
                                        {importResult.duplicates.length > 10 && ` et ${importResult.duplicates.length - 10} autres...`}
                                    </div>
                                </div>
                            )}

                            {/* Errors list */}
                            {importResult.errors.length > 0 && (
                                <div className="space-y-2">
                                    <Label className="text-red-600">Erreurs :</Label>
                                    <div className="max-h-32 overflow-y-auto text-sm bg-red-500/5 p-2 rounded space-y-1">
                                        {importResult.errors.slice(0, 10).map((err, idx) => (
                                            <div key={idx} className="text-red-600">
                                                Ligne {err.row}: {err.error}
                                            </div>
                                        ))}
                                        {importResult.errors.length > 10 && (
                                            <div className="text-muted-foreground">
                                                ... et {importResult.errors.length - 10} autres erreurs
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        {!importResult ? (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={resetImportModal}
                                    className="rounded-full"
                                >
                                    Annuler
                                </Button>
                                <Button
                                    onClick={handleImport}
                                    disabled={!importFile || importing}
                                    className="rounded-full"
                                    data-testid="confirm-import-contacts-btn"
                                >
                                    {importing ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Import en cours...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4 mr-2" />
                                            Importer
                                        </>
                                    )}
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={resetImportModal}
                                className="rounded-full"
                            >
                                Fermer
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Action Modal */}
            <Dialog open={showBulkActionModal} onOpenChange={setShowBulkActionModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {bulkAction === 'delete' && 'Supprimer les contacts sélectionnés'}
                            {bulkAction === 'assign_company' && "Changer d'entreprise"}
                            {bulkAction === 'set_primary' && 'Définir comme contact principal'}
                        </DialogTitle>
                        <DialogDescription>
                            Cette action sera appliquée à {selectedIds.length} contact(s)
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {bulkAction === 'delete' && (
                            <Alert variant="destructive">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription>
                                    Cette action supprimera définitivement les {selectedIds.length} contacts sélectionnés.
                                </AlertDescription>
                            </Alert>
                        )}

                        {bulkAction === 'assign_company' && (
                            <div className="space-y-2">
                                <Label>Nouvelle entreprise</Label>
                                <Select value={bulkActionValue} onValueChange={setBulkActionValue}>
                                    <SelectTrigger className="rounded-lg">
                                        <SelectValue placeholder="Sélectionner une entreprise" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {companies.map((c) => (
                                            <SelectItem key={c.id} value={c.id}>
                                                {c.legal_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    Les contacts seront déplacés vers cette entreprise et leur statut "principal" sera réinitialisé.
                                </p>
                            </div>
                        )}

                        {bulkAction === 'set_primary' && (
                            <Alert>
                                <UserCheck className="h-4 w-4" />
                                <AlertDescription>
                                    Chaque contact sélectionné deviendra le contact principal de son entreprise respective. 
                                    Les anciens contacts principaux perdront ce statut.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowBulkActionModal(false)}
                            className="rounded-full"
                        >
                            Annuler
                        </Button>
                        <Button
                            variant={bulkAction === 'delete' ? 'destructive' : 'default'}
                            onClick={handleBulkAction}
                            disabled={submitting || (bulkAction === 'assign_company' && !bulkActionValue)}
                            className="rounded-full"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                bulkAction === 'delete' ? 'Supprimer' : 'Appliquer'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Contacts;
